import { useState, useEffect } from "react";
import {
  Container,
  Group,
  Stack,
  Text,
  Title,
  ActionIcon,
  Table,
  Paper,
  Pagination,
  Badge,
  Avatar,
  Center,
  Skeleton,
  Modal,
  Button,
} from "@mantine/core";
import { IconRefresh, IconMessage } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { Endpoints } from "../../../lib/endpoints";
import { usePerformRequest } from "../../../hooks/usePerformRequest";

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  level?: number;
}

interface AppFeedback {
  _id: string;
  user: User;
  comment: string;
  createdAt: string;
}

interface FeedbackResponse {
  feedbacks: AppFeedback[];
  total: number;
  page: number;
  pages: number;
}

const FeedbackPage = () => {
  const [page, setPage] = useState(1);
  const limit = 20;
  const navigate = useNavigate();
  const [selectedFeedback, setSelectedFeedback] = useState<AppFeedback | null>(
    null,
  );
  const [modalOpened, setModalOpened] = useState(false);

  useEffect(() => {
    document.title = "App Feedback | Virel Admin";
  }, []);

  const { data, isLoading, reloadData } = usePerformRequest<{
    data: FeedbackResponse;
  }>({
    method: Endpoints.GetAppFeedback({ page, limit }).method,
    route: Endpoints.GetAppFeedback({ page, limit }).route,
  });

  const feedbacks = data?.data?.feedbacks || [];
  const totalPages = data?.data?.pages || 1;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2} fw={700}>
              App Feedback
            </Title>
            <Text c="dimmed" size="sm">
              View user feedback and reviews from the mobile app
            </Text>
          </div>
          <ActionIcon
            size="lg"
            variant="default"
            radius="md"
            onClick={() => reloadData()}
          >
            <IconRefresh size={18} />
          </ActionIcon>
        </Group>

        {isLoading ? (
          <Paper p="xl" withBorder radius="md">
            <Stack gap="md">
              <Skeleton height={50} radius="md" />
              <Skeleton height={50} radius="md" />
              <Skeleton height={50} radius="md" />
            </Stack>
          </Paper>
        ) : feedbacks.length === 0 ? (
          <Paper p="xl" withBorder radius="md" ta="center" bg="var(--bg-surface-2)">
            <Text c="dimmed">No feedback found.</Text>
          </Paper>
        ) : (
          <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
            <Table.ScrollContainer minWidth={800}>
              <Table verticalSpacing="sm" striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>User</Table.Th>
                    <Table.Th>Comment</Table.Th>
                    <Table.Th>Date</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {feedbacks.map((item) => (
                    <Table.Tr
                      key={item._id}
                      onClick={() => {
                        setSelectedFeedback(item);
                        setModalOpened(true);
                      }}
                      style={{ cursor: "pointer" }}
                      className="table-row-hover"
                    >
                      <Table.Td>
                        <Group gap="sm">
                          <Avatar
                            src={item.user?.avatar}
                            size={40}
                            radius={40}
                            color="blue"
                          >
                            {item.user?.firstName?.charAt(0)}
                            {item.user?.lastName?.charAt(0)}
                          </Avatar>
                          <div>
                            <Text size="sm" fw={500}>
                              {item.user?.firstName} {item.user?.lastName}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {item.user?.email}
                            </Text>
                            <Badge size="xs" variant="light" color="yellow" mt={2}>
                              Level {item.user?.level ?? 1}
                            </Badge>
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td style={{ maxWidth: 400 }}>
                        <Group gap="sm" wrap="nowrap">
                          <IconMessage
                            size={16}
                            color="gray"
                            style={{ flexShrink: 0 }}
                          />
                          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                            {item.comment}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {new Date(item.createdAt).toLocaleTimeString()}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            {totalPages > 0 && (
              <Group
                justify="center"
                p="md"
                style={{ borderTop: "1px solid #eee" }}
              >
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                  color="black"
                  radius="md"
                />
              </Group>
            )}
          </Paper>
        )}

        <Modal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          title="Feedback Details"
          centered
        >
          {selectedFeedback && (
            <Stack gap="md">
              <Group>
                <Avatar
                  src={selectedFeedback.user?.avatar}
                  size="lg"
                  radius="xl"
                  color="blue"
                >
                  {selectedFeedback.user?.firstName?.charAt(0)}
                  {selectedFeedback.user?.lastName?.charAt(0)}
                </Avatar>
                <div>
                  <Text fw={500}>
                    {selectedFeedback.user?.firstName}{" "}
                    {selectedFeedback.user?.lastName}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {selectedFeedback.user?.email}
                  </Text>
                  <Badge color="yellow" variant="light" size="sm" mt={4}>
                    Level {selectedFeedback.user?.level ?? 1}
                  </Badge>
                </div>
              </Group>

              <Text size="sm" c="dimmed">
                Submitted on:{" "}
                {new Date(selectedFeedback.createdAt).toLocaleString()}
              </Text>

              <Paper p="sm" withBorder bg="var(--bg-surface-2)">
                <Text style={{ whiteSpace: "pre-wrap" }} size="sm">
                  {selectedFeedback.comment}
                </Text>
              </Paper>

              <Group justify="flex-end" mt="md">
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(`/d/users/${selectedFeedback.user?._id}`)
                  }
                >
                  View User Profile
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Stack>
    </Container>
  );
};

export default FeedbackPage;
